import React, { useState, useEffect, lazy, Suspense, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import Header from "@/components/Header";
import SEOHead, { PAGE_SEO } from "@/components/SEOHead";
import CommodityTicker from "@/components/CommodityTicker";
import { HeaderStickyBanner } from "@/components/AdPlacements";
import Newsletter from "@/components/Newsletter";
import Footer from "@/components/Footer";
import NewsletterSignup from "@/components/NewsletterSignup";
import { Clock, ChatsCircle, ArrowRight } from "@phosphor-icons/react";

// Lazy load heavy below-fold components
const MarketDashboard = lazy(() => import("@/components/MarketDashboard"));
const TopOfTheMarts = lazy(() => import("@/components/TopOfTheMarts"));
const FarmingWeather = lazy(() => import("@/components/FarmingWeather"));
const StockNewsWidget = lazy(() => import("@/components/StockNews").then(m => ({ default: m.StockNewsWidget })));

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

// Compact Article Card Component
function ArticleCard({ article, showDiscuss = true }) {
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffHours = Math.floor((now - date) / (1000 * 60 * 60));
    if (diffHours < 1) return "Just now";
    if (diffHours < 24) return `${diffHours}h ago`;
    return date.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
  };

  return (
    <div className="flex flex-col h-full">
      <Link
        to={`/article/${article.id}`}
        className="flex-1 group"
        data-testid={`article-${article.id}`}
      >
        <span className="px-2 py-0.5 bg-[#0F766E] text-white text-[10px] font-mono uppercase tracking-wider">
          {article.category || "News"}
        </span>
        <h4 className="font-serif font-bold text-base text-[#33302E] mt-2 mb-1 line-clamp-2 group-hover:text-[#0F766E] transition-colors">
          {article.title}
        </h4>
        <p className="text-[#6B6661] text-sm mb-2 line-clamp-3">{article.summary}</p>
        <div className="flex items-center gap-2 text-xs text-[#6B6661]">
          <Clock size={12} />
          <span>{formatDate(article.published_at)}</span>
          <span>·</span>
          <span className="truncate">{article.source}</span>
        </div>
      </Link>
      {showDiscuss && (
        <Link
          to={`/community?article=${article.id}`}
          className="mt-3 inline-flex items-center gap-1.5 text-xs text-[#0F766E] hover:text-[#0D6B64] font-medium transition-colors"
          data-testid={`discuss-${article.id}`}
        >
          <ChatsCircle size={14} weight="bold" />
          Discuss
        </Link>
      )}
    </div>
  );
}

// Regional News Quadrant - 2x2 compact grid with More News link
function RegionalQuadrant({ title, articles, regionTag, moreLink }) {
  // Strictly limit to 4 articles for 2x2 grid
  const displayArticles = (articles || []).slice(0, 4);

  return (
    <div className="border border-[#E0D4C8] bg-white h-fit">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-[#E0D4C8] bg-gradient-to-r from-[#022C22] to-[#064E3B]">
        <h3 className="font-serif text-lg text-white">{title}</h3>
        <span className="text-[10px] font-mono uppercase tracking-wider text-[#6EE7B7]">{regionTag}</span>
      </div>
      
      {/* 2x2 Grid - exactly 4 articles */}
      {displayArticles.length > 0 ? (
        <div className="grid grid-cols-2">
          {displayArticles.map((article, i) => (
            <div
              key={article.id}
              className={`p-3 ${i % 2 === 0 ? "border-r border-[#E0D4C8]" : ""} ${i < 2 ? "border-b border-[#E0D4C8]" : ""}`}
            >
              <ArticleCard article={article} />
            </div>
          ))}
        </div>
      ) : (
        <div className="p-6 text-center text-[#6B6661]">
          <p className="text-sm">No articles available</p>
        </div>
      )}
      
      {/* More News Link */}
      {moreLink && (
        <Link 
          to={moreLink}
          className="flex items-center justify-center gap-2 py-3 border-t border-[#E0D4C8] text-[#0F766E] hover:text-[#0D6B64] hover:bg-[#F6E9DD] transition-colors font-medium text-sm"
        >
          <span>More {title}</span>
          <ArrowRight size={16} />
        </Link>
      )}
    </div>
  );
}

// Simple cache for session - prevents refetching on navigation back
const sessionCache = {
  commodities: null,
  globalMarkets: null,
  news: { ireland: null, uk: null, usa: null, world: null },
  timestamp: 0
};
const CACHE_TTL = 60000; // 1 minute cache

// Skeleton component for sidebar
function SidebarSkeleton() {
  return (
    <div className="border border-[#E0D4C8] bg-white p-4 animate-pulse">
      <div className="h-6 bg-[#E5E7EB] w-1/2 mb-4 rounded"></div>
      {[...Array(4)].map((_, i) => (
        <div key={i} className="h-10 bg-[#E5E7EB] mb-2 rounded"></div>
      ))}
    </div>
  );
}

export default function HomePage() {
  // Separate state for fast vs slow data
  const [newsLoading, setNewsLoading] = useState(true);
  const [tickerLoading, setTickerLoading] = useState(true);
  
  const [commodities, setCommodities] = useState(sessionCache.commodities || []);
  const [lambTicker, setLambTicker] = useState(sessionCache.lamb || null);
  const [irelandNews, setIrelandNews] = useState(sessionCache.news.ireland || []);
  const [ukNews, setUkNews] = useState(sessionCache.news.uk || []);
  const [usaNews, setUsaNews] = useState(sessionCache.news.usa || []);
  const [worldNews, setWorldNews] = useState(sessionCache.news.world || []);
  
  const hasFetched = useRef(false);

  useEffect(() => {
    // Prevent double fetch in strict mode
    if (hasFetched.current) return;
    hasFetched.current = true;
    
    // Check cache validity
    const now = Date.now();
    const cacheValid = (now - sessionCache.timestamp) < CACHE_TTL;
    
    if (cacheValid && sessionCache.news.ireland) {
      setNewsLoading(false);
      setTickerLoading(false);
      return;
    }
    
    // Fetch news first (fast APIs) - renders immediately
    fetchNews();
    // Fetch ticker data (slower APIs) - renders when ready
    fetchTickerData();
  }, []);

  const fetchNews = async () => {
    try {
      const [irelandRes, ukRes, usaRes, worldRes] = await Promise.allSettled([
        axios.get(`${API}/articles`, { params: { region: "ireland", limit: 4 } }),
        axios.get(`${API}/articles`, { params: { region: "uk", limit: 4 } }),
        axios.get(`${API}/articles`, { params: { region: "usa", limit: 4 } }),
        axios.get(`${API}/articles/world`, { params: { limit: 4 } })
      ]);
      
      const ireland = irelandRes.status === 'fulfilled' ? irelandRes.value.data || [] : [];
      const uk = ukRes.status === 'fulfilled' ? ukRes.value.data || [] : [];
      const usa = usaRes.status === 'fulfilled' ? usaRes.value.data || [] : [];
      const world = worldRes.status === 'fulfilled' ? worldRes.value.data || [] : [];
      
      setIrelandNews(ireland);
      setUkNews(uk);
      setUsaNews(usa);
      setWorldNews(world);
      
      // Update cache
      sessionCache.news = { ireland, uk, usa, world };
      sessionCache.timestamp = Date.now();
    } catch (error) {
      console.error("Error fetching news:", error);
    } finally {
      setNewsLoading(false);
    }
  };

  const fetchTickerData = async () => {
    try {
      const [commoditiesRes, sheepRes] = await Promise.allSettled([
        axios.get(`${API}/commodities`),
        axios.get(`${API}/uk/ahdb/sheep`, { timeout: 60000 }),
      ]);
      const comms = commoditiesRes.status === "fulfilled" ? (commoditiesRes.value.data || []) : [];

      const sheep = sheepRes.status === "fulfilled" ? sheepRes.value.data : null;
      const sqq = sheep?.available ? sheep.bands?.sqq : null;
      if (sqq?.average_price) {
        const change = sqq.price_change;
        const changePct = change !== null && change !== undefined && sqq.average_price !== change
          ? (change / (sqq.average_price - change)) * 100 : null;
        setLambTicker({
          symbol: "LAMB-SQQ",
          name: "GB Lamb SQQ (AHDB)",
          price: sqq.average_price,
          display_price: `${sqq.average_price.toFixed(1)}p/kg`,
          change: change ?? null,
          change_percent: changePct,
        });
        sessionCache.lamb = {
          symbol: "LAMB-SQQ",
          name: "GB Lamb SQQ (AHDB)",
          price: sqq.average_price,
          display_price: `${sqq.average_price.toFixed(1)}p/kg`,
          change: change ?? null,
          change_percent: changePct,
        };
      }

      setCommodities(comms);
      
      // Update cache
      sessionCache.commodities = comms;
    } catch (error) {
      console.error("Error fetching ticker data:", error);
    } finally {
      setTickerLoading(false);
    }
  };

  const tickerItems = React.useMemo(() => {
    if (!lambTicker) return commodities;
    const idx = commodities.findIndex((c) => c.symbol === "FEEDER" || c.symbol === "CATTLE");
    if (idx >= 0) return [...commodities.slice(0, idx + 1), lambTicker, ...commodities.slice(idx + 1)];
    return [...commodities, lambTicker];
  }, [commodities, lambTicker]);

  return (
    <div className="min-h-screen bg-[#FFF1E5]">
      <SEOHead {...PAGE_SEO.home} url="/" />
      
      <Header />
      
      {/* Hero Section - Loads Instantly */}
      <section className="bg-gradient-to-r from-[#022C22] via-[#064E3B] to-[#0F766E] py-8">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="text-center md:text-left">
              <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-white mb-3 leading-tight">
                Agricultural Intelligence<br />
                <span className="text-[#5EEAD4]">For Modern Farmers</span>
              </h1>
              <p className="text-[#A7F3D0] text-lg max-w-xl">
                Real-time market prices, news, and insights from Ireland, UK, USA & beyond
              </p>
            </div>
            <div className="flex gap-3">
              <Link 
                to="/irish-marts" 
                className="px-6 py-3 bg-white text-[#022C22] font-medium hover:bg-[#F0FDF4] transition-colors"
              >
                View Markets
              </Link>
              <Link 
                to="/marketplace" 
                className="px-6 py-3 border-2 border-white text-white font-medium hover:bg-white/10 transition-colors"
              >
                Buy & Sell
              </Link>
            </div>
          </div>
        </div>
      </section>
      
      {/* Commodity Ticker - shows placeholder while loading */}
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-3">
        {tickerLoading ? (
          <div className="bg-[#F0FDF4] border border-[#E0D4C8] rounded h-10 animate-pulse flex items-center justify-center">
            <span className="text-[#6B6661] text-xs">Loading commodities...</span>
          </div>
        ) : (
          <CommodityTicker commodities={tickerItems} />
        )}
      </div>
      
      <main>
        {/* Regional News Grid - 2x2 Quadrants */}
        <section className="py-6">
          <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
            {newsLoading ? (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {[...Array(4)].map((_, i) => (
                  <div key={`skeleton-${i}`} className="border border-[#E0D4C8] p-4 animate-pulse">
                    <div className="h-4 bg-[#E5E7EB] w-1/4 mb-3"></div>
                    <div className="grid grid-cols-2 gap-4">
                      {[...Array(4)].map((_, j) => (
                        <div key={`skeleton-item-${i}-${j}`} className="space-y-2">
                          <div className="h-3 bg-[#E5E7EB] w-1/3"></div>
                          <div className="h-4 bg-[#E5E7EB] w-full"></div>
                          <div className="h-3 bg-[#E5E7EB] w-2/3"></div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col xl:flex-row gap-4">
                {/* Main News Grid */}
                <div className="flex-1 space-y-4">
                  {/* Top Row: Ireland & UK */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {/* Ireland Quadrant */}
                    <RegionalQuadrant 
                      title="Ireland" 
                      articles={irelandNews} 
                      regionTag="IRELAND"
                      moreLink="/irish-news"
                    />

                    {/* UK Quadrant */}
                    <RegionalQuadrant 
                      title="United Kingdom" 
                      articles={ukNews} 
                      regionTag="UK"
                      moreLink="/uk-news"
                    />
                  </div>

                  {/* Bottom Row: USA & Rest of World */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {/* USA Quadrant */}
                    <RegionalQuadrant 
                      title="United States" 
                      articles={usaNews} 
                      regionTag="USA"
                      moreLink="/us-news"
                    />

                    {/* Rest of World Quadrant */}
                    <RegionalQuadrant 
                      title="Rest of World" 
                      articles={worldNews} 
                      regionTag="GLOBAL"
                      moreLink="/global-news"
                    />
                  </div>
                </div>

                {/* Sidebar - Lazy loaded with Suspense */}
                <div className="xl:w-72 flex-shrink-0 flex flex-col gap-4">
                  <Suspense fallback={<SidebarSkeleton />}>
                    <TopOfTheMarts />
                  </Suspense>
                  <Suspense fallback={<SidebarSkeleton />}>
                    <FarmingWeather />
                  </Suspense>
                  <Suspense fallback={<SidebarSkeleton />}>
                    <StockNewsWidget limit={4} />
                  </Suspense>
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Newsletter Signup */}
        <NewsletterSignup />

        {/* Market Dashboard - Dark Theme Section (Lazy loaded) */}
        <section id="markets" className="py-12 bg-[#022C22]">
          <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between mb-8">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <span className="px-2 py-1 bg-[#0F766E] text-white text-xs font-mono uppercase tracking-wider">
                    Markets
                  </span>
                  <span className="px-2 py-1 bg-[#FBBF24] text-[#022C22] text-xs font-mono uppercase tracking-wider">
                    Live Data
                  </span>
                </div>
                <h2 className="font-serif text-2xl sm:text-3xl text-white">
                  Markets & Commodities
                </h2>
              </div>
            </div>
            <Suspense fallback={
              <div className="h-96 flex items-center justify-center">
                <div className="text-center">
                  <div className="animate-spin h-8 w-8 border-2 border-[#6EE7B7] border-t-transparent rounded-full mx-auto mb-4"></div>
                  <p className="text-[#6EE7B7]">Loading market dashboard...</p>
                </div>
              </div>
            }>
              <MarketDashboard commodities={commodities} />
            </Suspense>
          </div>
        </section>

        {/* Newsletter */}
        <Newsletter />
      </main>

      <Footer />
    </div>
  );
}
